import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from 'react-select';
import AppConst from '../apis/appconst';

function AddServiceGroup({ item, allServices, onSaved }) {
    const [groupName, setGroupName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedServices, setSelectedServices] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    // ✅ NEW: ward + bed state
    const [wards, setWards] = useState([]);           // wards each with nested beds
    const [beds, setBeds] = useState([]);             // flat bed list (each carries ward info)
    const [selectedWard, setSelectedWard] = useState(null);
    const [selectedBed, setSelectedBed] = useState(null);

    const isEdit = item && item.cat_id;

    // Build service options for the select
    const serviceOptions = useMemo(() => {
        return (allServices || []).map(s => ({
            value: s.cat_id,
            label: s.cat_name + (s.price ? ` (₹${s.price})` : '')
        }));
    }, [allServices]);

    // ✅ NEW: load wards + beds together (ward has nested beds)
    const loadWardsAndBeds = async () => {
        try {
            const fd = new FormData();
            fd.append('par_by', '1');
            const resp = await axios.post(
                AppConst.BASE_URL + AppConst.getBedListUrl1,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (resp?.data?.status) {
                const records = resp.data.records || [];
                setWards(records);

                // flatten beds, tagging each with its ward
                const flat = [];
                records.forEach(w => {
                    (w.beds || []).forEach(b => {
                        flat.push({
                            ...b,
                            ward_id: w.cat_id,
                            ward_name: w.cat_name,
                        });
                    });
                });
                setBeds(flat);
            }
        } catch (e) {
            console.error('loadWardsAndBeds', e);
        }
    };

    useEffect(() => {
        loadWardsAndBeds();
    }, []);

    // ✅ NEW: ward options
    const wardOptions = useMemo(
        () => (wards || []).map(w => ({ value: w.cat_id, label: w.cat_name, raw: w })),
        [wards]
    );

    // ✅ NEW: bed options (filtered by selected ward if any, else all beds)
    const bedOptions = useMemo(() => {
        const list = selectedWard
            ? beds.filter(b => String(b.ward_id) === String(selectedWard.value))
            : beds;
        return list.map(b => ({
            value: b.bed_id,
            label: `${b.bed_name}${b.ward_name ? ` — ${b.ward_name}` : ''}`,
            raw: b,
        }));
    }, [beds, selectedWard]);

    // ✅ NEW: selecting a ward → filter beds; clear bed if it no longer matches
    const onSelectWard = (opt) => {
        setSelectedWard(opt || null);
        if (opt && selectedBed && String(selectedBed.raw?.ward_id) !== String(opt.value)) {
            setSelectedBed(null);
        }
    };

    // ✅ NEW: selecting a bed → auto-select its ward
    const onSelectBed = (opt) => {
        setSelectedBed(opt || null);
        if (opt?.raw?.ward_id) {
            const wOpt = wardOptions.find(w => String(w.value) === String(opt.raw.ward_id));
            if (wOpt) setSelectedWard(wOpt);
        }
    };

    // When item prop changes, prefill the form
    useEffect(() => {
        if (isEdit) {
            setGroupName(item.cat_name || '');
            setDescription(item.cat_type2 || '');

            const linkedIds = (item.services || []).map(s => s.cat_id);
            setSelectedServices(
                serviceOptions.filter(o => linkedIds.includes(o.value))
            );
        } else {
            setGroupName('');
            setDescription('');
            setSelectedServices([]);
        }
    }, [item, serviceOptions, isEdit]);

    // ✅ NEW: prefill ward/bed on edit (best-effort — matches whatever key backend returns)
    useEffect(() => {
        if (!isEdit) {
            setSelectedWard(null);
            setSelectedBed(null);
            return;
        }
        const wardId = item.ipd_ward ?? item.ward_id ?? item.cat_ward ?? '';
        const bedId  = item.ipd_bed  ?? item.bed_id  ?? item.cat_bed  ?? '';

        if (bedId) {
            const bOpt = beds
                .map(b => ({
                    value: b.bed_id,
                    label: `${b.bed_name}${b.ward_name ? ` — ${b.ward_name}` : ''}`,
                    raw: b,
                }))
                .find(o => String(o.value) === String(bedId));
            if (bOpt) {
                setSelectedBed(bOpt);
                const wOpt = wardOptions.find(w => String(w.value) === String(bOpt.raw.ward_id));
                if (wOpt) setSelectedWard(wOpt);
                return;
            }
        }
        if (wardId) {
            const wOpt = wardOptions.find(w => String(w.value) === String(wardId));
            if (wOpt) setSelectedWard(wOpt);
        }
    }, [item, isEdit, beds, wardOptions]);

    const handleSave = async () => {
        if (!groupName.trim()) {
            toast.error('Group name is required');
            return;
        }

        setIsSaving(true);
        try {
            const fd = new FormData();
            if (isEdit) fd.append('cat_id', item.cat_id);
            fd.append('cat_name', groupName.trim());
            fd.append('cat_type2', description.trim());
            fd.append('service_ids', JSON.stringify(selectedServices.map(s => s.value)));

            // ✅ NEW: send selected ward + bed (backend can store if columns exist)
            fd.append('ward_id', selectedWard ? selectedWard.value : '');
            fd.append('ward_name', selectedWard ? selectedWard.label : '');
            fd.append('bed_id', selectedBed ? selectedBed.value : '');
            fd.append('bed_name', selectedBed ? (selectedBed.raw?.bed_name || '') : '');

            const resp = await axios.post(
                AppConst.BASE_URL + AppConst.saveServiceGroupUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (resp?.data?.status) {
                toast.success(resp.data.success || 'Saved');
                const closeBtn = document.getElementById('closeGroupModal');
                if (closeBtn) closeBtn.click();
                if (typeof onSaved === 'function') onSaved();
            } else {
                toast.error(resp?.data?.success || 'Failed to save group');
            }
        } catch (e) {
            console.error(e);
            toast.error('Failed to save group');
        } finally {
            setIsSaving(false);
        }
    };

    const reactSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '38px',
            fontSize: '14px',
            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
        }),
        valueContainer: (base) => ({
            ...base,
            maxHeight: '150px',
            overflowY: 'auto',
        }),
        multiValue: (base) => ({ ...base, backgroundColor: '#e7f1ff', borderRadius: '3px' }),
        multiValueLabel: (base) => ({ ...base, color: '#0d6efd', fontSize: '12px' }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
        menuList: (base) => ({
            ...base,
            maxHeight: '220px',
            overflowY: 'auto',
            paddingBottom: '12px',
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    return (
        <>
            <button
                id="addgrpbt789"
                type="button"
                style={{ display: 'none' }}
                data-bs-toggle="modal"
                data-bs-target="#serviceGroupModal"
            />

            <div
                className="modal fade"
                id="serviceGroupModal"
                tabIndex="-1"
                aria-labelledby="serviceGroupModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header bg-light">
                            <h5 className="modal-title" id="serviceGroupModalLabel">
                                <i className="ri-folder-2-line me-2"></i>
                                {isEdit ? 'Edit Service Group' : 'Add Service Group'}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                id="closeGroupModal"
                            />
                        </div>

                        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', paddingBottom: '80px' }}>
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label fw-bold">
                                        Group Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g. Hospital, OT, Lab Services"
                                        value={groupName}
                                        onChange={(e) => setGroupName(e.target.value)}
                                        autoFocus
                                    />
                                </div>

                                <div className="col-md-12">
                                    <label className="form-label">Description (optional)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Short description of this group"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </div>

                                {/* ✅ NEW: Ward + Bed (searchable) — like OPD Registration */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Select Ward</label>
                                    <Select
                                        options={wardOptions}
                                        value={selectedWard}
                                        onChange={onSelectWard}
                                        isClearable
                                        isSearchable
                                        placeholder="Select ward..."
                                        styles={reactSelectStyles}
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        menuPosition="fixed"
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Select Bed</label>
                                    <Select
                                        options={bedOptions}
                                        value={selectedBed}
                                        onChange={onSelectBed}
                                        isClearable
                                        isSearchable
                                        placeholder={selectedWard ? 'Select bed...' : 'Select bed (or pick ward first)...'}
                                        styles={reactSelectStyles}
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        menuPosition="fixed"
                                        noOptionsMessage={() => 'No beds found'}
                                    />
                                    <small className="text-muted">
                                        Picking a bed auto-selects its ward.
                                    </small>
                                </div>

                                <div className="col-md-12">
                                    <label className="form-label fw-bold">
                                        Select Services <span className="text-muted small">({selectedServices.length} selected)</span>
                                    </label>
                                    <Select
                                        isMulti
                                        options={serviceOptions}
                                        value={selectedServices}
                                        onChange={(s) => setSelectedServices(s || [])}
                                        placeholder="Choose services to include in this group..."
                                        isClearable
                                        isSearchable
                                        styles={reactSelectStyles}
                                        closeMenuOnSelect={false}
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        menuPosition="fixed"
                                        menuPlacement="auto"
                                        maxMenuHeight={220}
                                        menuShouldScrollIntoView={false}
                                    />
                                    <small className="text-muted">
                                        Services already assigned to another group will be moved to this one.
                                    </small>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-light"
                                data-bs-dismiss="modal"
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
                                ) : (
                                    <><i className="ri-save-line me-1"></i>{isEdit ? 'Update Group' : 'Create Group'}</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AddServiceGroup;